import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useDataProvider, useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SeatOpsViewer from '../components/seat-ops/SeatOpsViewer';
import { pullEventFromFirebase } from '../services/firebasePullService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../database/firebase';
import {
  pushEventLiveStateToFirebase,
  pushSeatLiveStateToFirebase,
  pushSeatsLiveStateToFirebase,
  pushTicketAndSeatsToFirebase,
  fixEventDetailsForTicketPrint,
} from '../services/firebasePushService';
import { useFirebasePushAuth } from '../components/misc/FirebasePushGate';
import {
  analyzeSeatConflicts,
  collectLayoutSeatMeta,
  getTicketSeats,
  normalizeSeatSelections,
} from '../utils/seatConflictUtils';
import { stringToColour } from '../components/seating/utils/seatingUtils';
import { formatUserFullName, isTicketRefunded } from '../utils/ticketsUtils';
import { findSalesForSeat } from '../utils/seatOpsSaleUtils';
import { buildSoldSeatSelectionsFromTickets } from '../utils/seatOpsBulkUtils';
import { generateTicketID } from '../utils/formatting';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { exportSeatOpsExcel } from '../utils/seatOpsExport';

function productColour(id) {
  return stringToColour(String(id));
}

const SeatOpsPage = () => {
  const [searchParams] = useSearchParams();
  const eventID = searchParams.get('eventID');
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { requirePushPassword, PushPasswordDialog } = useFirebasePushAuth();

  const [loading, setLoading] = useState(true);
  const [pulling, setPulling] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [eventLayout, setEventLayout] = useState(null);
  const [stock, setStock] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [seatSelections, setSeatSelections] = useState(null);
  /** Firebase stocks/{id}.seatSelections — 2. yer (canli web) */
  const [stockFbSeatSelections, setStockFbSeatSelections] = useState(null);
  const [pulledAt, setPulledAt] = useState(null);
  const [selectedSeatId, setSelectedSeatId] = useState(null);
  const [clearingHold, setClearingHold] = useState(false);
  const [assigningSel, setAssigningSel] = useState(false);
  const [seatEdits, setSeatEdits] = useState({}); // ticketId -> newSeatId
  const [savingSeat, setSavingSeat] = useState(null);
  const [saleMatches, setSaleMatches] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [eventSales, setEventSales] = useState([]);
  const [seatSelUser, setSeatSelUser] = useState(null);
  const [loadingSeatSelUser, setLoadingSeatSelUser] = useState(false);
  const [manualUserId, setManualUserId] = useState('');
  const [manualUserPreview, setManualUserPreview] = useState(null);
  const [loadingManualUser, setLoadingManualUser] = useState(false);
  const [assigningSale, setAssigningSale] = useState(false);
  const [bulkFilling, setBulkFilling] = useState(false);
  const [refundingTicketId, setRefundingTicketId] = useState(null);
  const [fixingTicketPrint, setFixingTicketPrint] = useState(false);
  const pullingRef = useRef(false);

  const loadAll = useCallback(async () => {
    if (!eventID) return;
    setLoading(true);
    setError(null);
    try {
      const { data: ev } = await dataProvider.getOne('events-v2', { id: eventID });
      setEvent(ev);

      let el = null;
      const elId = ev.layoutJSONID || ev.layoutID;
      if (elId) {
        try {
          const res = await dataProvider.getOne('eventLayouts', { id: elId });
          el = res.data;
        } catch (e) {
          console.warn('eventLayout load failed', e);
        }
      }
      setEventLayout(el);

      const { data: stocks } = await dataProvider.getList('stocks', {
        filter: { event: eventID },
        pagination: { page: 1, perPage: 50 },
        sort: { field: 'id', order: 'ASC' },
      });
      const primaryStock = (stocks && stocks[0]) || null;
      setStock(primaryStock);

      let selections = null;
      if (primaryStock?.id) {
        try {
          const selRes = await dataProvider.getOne('seatSelections', { id: primaryStock.id });
          selections = selRes.data;
        } catch {
          selections = null;
        }
      }
      setSeatSelections(selections);

      // 2. yer: Firebase stocks.seatSelections (canli web)
      let fbStockSel = null;
      if (primaryStock?.id) {
        try {
          const snap = await getDoc(doc(db, 'stocks', primaryStock.id));
          fbStockSel = snap.exists() ? snap.data()?.seatSelections || {} : {};
        } catch (e) {
          console.warn('Firebase stocks.seatSelections load failed', e);
          fbStockSel = null;
        }
      }
      setStockFbSeatSelections(fbStockSel);

      const { data: ticketRows } = await dataProvider.getList('tickets', {
        filter: { 'event.id': eventID },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'created', order: 'DESC' },
      });
      const normalized = (ticketRows || [])
        .filter((t) => !isTicketRefunded(t))
        .map((t) => ({
          ...t,
          seats: getTicketSeats(t),
        }));
      setTickets(normalized);

      // Satislar (order / kim aldi) — iade (status 3) gosterme
      let salesCombined = [];
      try {
        const { data: s1 } = await dataProvider.getList('sales', {
          filter: { 'event.id': eventID },
          pagination: { page: 1, perPage: 500 },
          sort: { field: 'id', order: 'DESC' },
        });
        salesCombined = salesCombined.concat(s1 || []);
      } catch (e) {
        console.warn('sales load failed', e);
      }
      try {
        const { data: s2 } = await dataProvider.getList('sales-v2', {
          filter: { 'event.id': eventID },
          pagination: { page: 1, perPage: 500 },
          sort: { field: 'id', order: 'DESC' },
        });
        salesCombined = salesCombined.concat(s2 || []);
      } catch (e) {
        console.warn('sales-v2 load failed', e);
      }
      // id bazinda tekillestir — iade status 3 haric
      const byId = new Map();
      salesCombined.forEach((s) => {
        if (!s?.id) return;
        if (Number(s.status) === 3 || s?.extras?.refunded === true) return;
        byId.set(s.id, s);
      });
      setEventSales(Array.from(byId.values()));
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [dataProvider, eventID]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handlePull = useCallback(async () => {
    if (!eventID || pullingRef.current) return;
    const ok = window.confirm(
      `Firebase'den SON GÜNCEL hali komple çekilecek (REPLACE):\nevents-v2/${eventID}\n\n` +
        `• tickets/sales silinir → Firebase'den yeniden yazılır\n` +
        `• Postgres'te İADE işaretli kayıtlar KORUNUR (silinmez / FB üzerine yazmaz)\n` +
        `• Firebase push YOK\n\nDevam?`,
    );
    if (!ok) return;

    pullingRef.current = true;
    setPulling(true);
    try {
      const result = await pullEventFromFirebase(eventID, dataProvider);
      setPulledAt(result.pulledAt);
      const conflictCount = result.conflicts?.length || 0;
      notify(
        `Replace OK: ${result.counts.tickets} FB bilet` +
          (result.counts.preservedTickets
            ? `, ${result.counts.preservedTickets} iade korundu`
            : '') +
          (result.counts.preservedSales
            ? `, ${result.counts.preservedSales} iade satış korundu`
            : '') +
          (conflictCount ? ` — ${conflictCount} uyarı!` : ''),
        { type: conflictCount ? 'warning' : 'success' },
      );
      await loadAll();
    } catch (err) {
      console.error(err);
      notify(`Pull hata: ${err?.message || err}`, { type: 'error' });
    } finally {
      pullingRef.current = false;
      setPulling(false);
    }
  }, [eventID, dataProvider, notify, loadAll]);

  const layoutJSON = useMemo(() => {
    const raw = eventLayout?.layoutJSON;
    if (!raw) return null;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
    return raw;
  }, [eventLayout]);

  const layoutSeatMeta = useMemo(() => collectLayoutSeatMeta(layoutJSON), [layoutJSON]);

  const analysis = useMemo(
    () =>
      analyzeSeatConflicts({
        tickets,
        layoutJSON,
        seatSelections,
        stockFirebaseSeatSelections: stockFbSeatSelections,
      }),
    [tickets, layoutJSON, seatSelections, stockFbSeatSelections],
  );

  const stockFbMismatchCount = analysis.stockFbMismatchSeatIds?.length || 0;

  const adminBlockedWithTicketSeatIds = useMemo(() => {
    const ids = analysis.blockedSeatIds || [];
    return ids
      .filter((seatId) => (analysis.seatsByTicket?.get?.(seatId)?.length || 0) > 0)
      .slice()
      .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  }, [analysis]);

  const products = useMemo(() => {
    const stockArr = Array.isArray(stock?.stock) ? stock.stock : [];
    return stockArr;
  }, [stock]);

  const selectedTickets = useMemo(() => {
    if (!selectedSeatId) return [];
    return analysis.seatsByTicket.get(selectedSeatId) || [];
  }, [selectedSeatId, analysis]);

  const selectedHold = useMemo(() => {
    if (!selectedSeatId) return null;
    const map = normalizeSeatSelections(seatSelections);
    const sel = map[selectedSeatId];
    if (sel && Number(sel.status) === 2) return sel;
    return null;
  }, [selectedSeatId, seatSelections]);

  const selectedSelStatus = useMemo(() => {
    if (!selectedSeatId) return null;
    const map = normalizeSeatSelections(seatSelections);
    const sel = map[selectedSeatId];
    if (!sel) return null;
    return Number(sel.status);
  }, [selectedSeatId, seatSelections]);

  const selectedSeatSel = useMemo(() => {
    if (!selectedSeatId) return null;
    const map = normalizeSeatSelections(seatSelections);
    return map[selectedSeatId] || null;
  }, [selectedSeatId, seatSelections]);

  // Satilmis + biletsiz: order / sale eslestir
  useEffect(() => {
    if (!selectedSeatId) {
      setSaleMatches([]);
      return;
    }
    if (selectedTickets.length > 0) {
      setSaleMatches([]);
      return;
    }
    if (selectedSelStatus !== 1) {
      setSaleMatches([]);
      return;
    }
    setLoadingSales(true);
    try {
      const userId = selectedSeatSel?.user || null;
      const matches = findSalesForSeat({
        sales: eventSales,
        seatId: selectedSeatId,
        userId,
      });
      setSaleMatches(matches);
    } finally {
      setLoadingSales(false);
    }
  }, [
    selectedSeatId,
    selectedTickets.length,
    selectedSelStatus,
    selectedSeatSel,
    eventSales,
  ]);

  // seatSelections.user -> users (manuel butonla da yuklenebilir)
  const loadSeatSelUser = useCallback(async () => {
    const userId = selectedSeatSel?.user;
    if (!userId) {
      setSeatSelUser(null);
      notify('Bu koltukta seatSelections.user yok', { type: 'warning' });
      return;
    }
    setLoadingSeatSelUser(true);
    try {
      const { data: u } = await dataProvider.getOne('users', { id: String(userId) });
      setSeatSelUser(u);
    } catch {
      setSeatSelUser({ id: String(userId), _missing: true });
      notify(`User bulunamadı: ${userId}`, { type: 'warning' });
    } finally {
      setLoadingSeatSelUser(false);
    }
  }, [selectedSeatSel?.user, dataProvider, notify]);

  useEffect(() => {
    setSeatSelUser(null);
    const userId = selectedSeatSel?.user;
    if (!userId) {
      setLoadingSeatSelUser(false);
      return undefined;
    }
    // Hold / satilmis: otomatik cek (buton da var)
    let cancelled = false;
    setLoadingSeatSelUser(true);
    (async () => {
      try {
        const { data: u } = await dataProvider.getOne('users', { id: String(userId) });
        if (!cancelled) setSeatSelUser(u);
      } catch {
        if (!cancelled) setSeatSelUser({ id: String(userId), _missing: true });
      } finally {
        if (!cancelled) setLoadingSeatSelUser(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSeatSel?.user, dataProvider]);

  // Manuel user id preview
  useEffect(() => {
    let cancelled = false;
    const uid = String(manualUserId || '').trim();
    if (!uid || uid.length < 8) {
      setManualUserPreview(null);
      setLoadingManualUser(false);
      return undefined;
    }
    const t = setTimeout(async () => {
      setLoadingManualUser(true);
      try {
        const { data: u } = await dataProvider.getOne('users', { id: uid });
        if (!cancelled) setManualUserPreview(u);
      } catch {
        if (!cancelled) setManualUserPreview({ id: uid, _missing: true });
      } finally {
        if (!cancelled) setLoadingManualUser(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [manualUserId, dataProvider]);

  // Koltuk degisince manuel user sifirla
  useEffect(() => {
    setManualUserId('');
    setManualUserPreview(null);
  }, [selectedSeatId]);

  const generateSaleDocId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let out = '';
    for (let i = 0; i < 20; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
    return out;
  };

  const buildEventRefForTicket = () => {
    const sample = tickets.find((t) => t?.event?.session);
    return {
      id: event.id,
      name: event.name,
      venue: event.venue || null,
      city: event.city || null,
      category: event.category || null,
      subcategories: event.subcategories || [],
      subcategory: '',
      organisationCompanies: event.organisationCompanies || [],
      stockID: stock.id,
      session: sample?.event?.session || null,
      type: event.type,
    };
  };

  const pushLiveWithPassword = async () => {
    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli) — Postgres/Firebase yazılmadı', { type: 'warning' });
      return false;
    }
    setPushing(true);
    try {
      const result = await pushEventLiveStateToFirebase(eventID, dataProvider);
      notify(
        `Firebase push OK: ${result.counts.tickets} bilet, ${result.counts.stocks} stock`,
        { type: 'success' },
      );
      return true;
    } catch (err) {
      console.error(err);
      notify(`Firebase push hata: ${err?.message || err}`, { type: 'error' });
      return false;
    } finally {
      setPushing(false);
    }
  };

  /** Secili koltuk: sifre -> Firebase (sadece bu seat + ilgili biletler) */
  const pushSelectedSeatWithPassword = async () => {
    if (!stock?.id || !selectedSeatId) return false;
    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli) — Firebase yazılmadı', { type: 'warning' });
      return false;
    }
    setPushing(true);
    try {
      const result = await pushSeatLiveStateToFirebase({
        eventId: eventID,
        stockId: stock.id,
        seatId: selectedSeatId,
        dataProvider,
      });
      notify(
        `Firebase push OK: ${selectedSeatId} · sadece seatSelections`,
        { type: 'success' },
      );
      return true;
    } catch (err) {
      console.error(err);
      notify(`Firebase push hata: ${err?.message || err}`, { type: 'error' });
      return false;
    } finally {
      setPushing(false);
    }
  };

  const missingSoldFlagCount = useMemo(() => {
    return (analysis.conflicts || []).filter((c) => c.type === 'ticket_without_sold_flag')
      .length;
  }, [analysis.conflicts]);

  const handleBulkFillSoldFlags = async () => {
    if (!stock?.id || bulkFilling || pulling || pushing) return;
    const preview = buildSoldSeatSelectionsFromTickets({
      tickets,
      seatSelections,
    });
    const syncSeats = analysis.stockFbMismatchSeatIds || [];
    if (!preview.filled.length && !syncSeats.length) {
      notify('Düzeltilecek yok — flag eksik / 2. yer uyumsuzluğu yok', {
        type: 'info',
      });
      return;
    }
    const ok = window.confirm(
      `TOPLU DÜZELTME\n\n` +
        (preview.filled.length
          ? `• ${preview.filled.length} koltuk: Postgres seatSelections → 1\n` +
            `  Örnek: ${preview.filled.slice(0, 10).join(', ')}${preview.filled.length > 10 ? '…' : ''}\n`
          : '') +
        (syncSeats.length
          ? `• ${syncSeats.length} koltuk: 2. yer (stocks) sync\n` +
            `  Örnek: ${syncSeats.slice(0, 10).join(', ')}${syncSeats.length > 10 ? '…' : ''}\n`
          : '') +
        `\nHepsi Firebase seatSelections + stocks’a yazılır.\nŞifre sorulacak.\nDevam?`,
    );
    if (!ok) return;
    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli)', { type: 'warning' });
      return;
    }

    setBulkFilling(true);
    setPushing(true);
    try {
      if (preview.filled.length) {
        await dataProvider.update('seatSelections', {
          id: stock.id,
          data: preview.next,
        });
        notify(`Postgres: ${preview.filled.length} koltuk → status 1`, {
          type: 'success',
        });
      }

      const seatIdsToPush = [
        ...new Set([...(preview.filled || []), ...syncSeats]),
      ];
      await pushSeatsLiveStateToFirebase({
        eventId: eventID,
        stockId: stock.id,
        seatIds: seatIdsToPush,
        dataProvider,
      });
      notify(
        `Firebase OK: ${seatIdsToPush.length} koltuk (seatSelections + stocks / 2. yer)`,
        { type: 'success' },
      );
      await loadAll();
    } catch (err) {
      console.error(err);
      notify(`Toplu atama hata: ${err?.message || err}`, { type: 'error' });
    } finally {
      setBulkFilling(false);
      setPushing(false);
    }
  };

  /** Postgres dogru, Firebase stocks (2. yer) eksik/farkli → sadece o koltuklari push */
  const handleSyncStockFbMismatches = async () => {
    if (!stock?.id || !eventID || pushing || bulkFilling) return;
    const seats = analysis.stockFbMismatchSeatIds || [];
    if (!seats.length) {
      notify('2. yer (stocks) uyumsuzluğu yok', { type: 'info' });
      return;
    }
    const ok = window.confirm(
      `Postgres → Firebase 2. yer sync\n\n` +
        `${seats.length} koltuk: seatSelections + stocks.seatSelections yazılacak.\n` +
        `Örnek: ${seats.slice(0, 15).join(', ')}${seats.length > 15 ? '…' : ''}\n\n` +
        `Şifre sorulacak. Devam?`,
    );
    if (!ok) return;
    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli)', { type: 'warning' });
      return;
    }
    setPushing(true);
    try {
      await pushSeatsLiveStateToFirebase({
        eventId: eventID,
        stockId: stock.id,
        seatIds: seats,
        dataProvider,
      });
      notify(`2. yer sync OK: ${seats.length} koltuk`, { type: 'success' });
      await loadAll();
    } catch (err) {
      console.error(err);
      notify(`2. yer sync hata: ${err?.message || err}`, { type: 'error' });
    } finally {
      setPushing(false);
    }
  };

  const handlePush = async () => {
    if (!eventID || pushing) return;
    const ok = window.confirm(
      `Postgres'teki bilet + seatSelections Firebase'e yazılacak.\nŞifre sorulacak. Şifre yoksa hiçbir şey yazılmaz.\nDevam?`,
    );
    if (!ok) return;
    await pushLiveWithPassword();
  };

  /** Canli PDF: events-v2.details HTML'ini sadeleştir (react-pdf patlamasin) */
  const handleFixTicketPrint = async () => {
    if (!eventID || fixingTicketPrint || pushing) return;
    const ok = window.confirm(
      `Biletleri aç (canlı PDF hotfix)\n\n` +
        `Firebase events-v2/${eventID}\n` +
        `details HTML sadeleştirilecek (<em>, &nbsp; vb. temizlenir).\n` +
        `Bilet / sale / koltuk DOKUNULMAZ.\n\nŞifre sorulacak. Devam?`,
    );
    if (!ok) return;
    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli) — yazılmadı', { type: 'warning' });
      return;
    }
    setFixingTicketPrint(true);
    try {
      const result = await fixEventDetailsForTicketPrint(eventID);
      notify(
        `Biletleri aç OK: details ${result.beforeLen.tr} → ${result.afterLen.tr} karakter` +
          (result.hadRiskyMarkup ? ' (riskli HTML temizlendi)' : ''),
        { type: 'success' },
      );
    } catch (err) {
      console.error(err);
      notify(`Biletleri aç hata: ${err?.message || err}`, { type: 'error' });
    } finally {
      setFixingTicketPrint(false);
    }
  };

  const handlePushSelectedSeat = async () => {
    if (!selectedSeatId || !stock?.id || pushing) return;
    const ok = window.confirm(
      `Seçili koltuk Firebase seatSelections'a yazılacak:\n${selectedSeatId}\n\nSadece seatSelections (ticket yok).\nŞifre sorulacak.\nDevam?`,
    );
    if (!ok) return;
    await pushSelectedSeatWithPassword();
  };

  /** Secili bileti + koltuk(lar)ini Firebase'e yaz */
  const handlePushTicketToFirebase = async (ticket) => {
    if (!ticket?.id || !stock?.id || !eventID || pushing) return;
    const seatIds = getTicketSeats(ticket)
      .map((s) => s?.id)
      .filter(Boolean);
    if (selectedSeatId && !seatIds.includes(selectedSeatId)) {
      seatIds.push(selectedSeatId);
    }
    const ok = window.confirm(
      `Bilet Firebase'e yazılacak:\nPNR ${ticket.pnr || ticket.id}\n` +
        `Koltuklar: ${seatIds.join(', ') || '—'}\n\nŞifre sorulacak.\nDevam?`,
    );
    if (!ok) return;
    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli) — Firebase yazılmadı', { type: 'warning' });
      return;
    }
    setPushing(true);
    try {
      const result = await pushTicketAndSeatsToFirebase({
        eventId: eventID,
        stockId: stock.id,
        ticketId: ticket.id,
        seatIds,
        dataProvider,
      });
      notify(
        `Firebase OK: bilet ${ticket.pnr || ticket.id} · ${result.counts.seatSelections} koltuk`,
        { type: 'success' },
      );
    } catch (err) {
      console.error(err);
      notify(`Firebase push hata: ${err?.message || err}`, { type: 'error' });
    } finally {
      setPushing(false);
    }
  };

  const updateSeatSelectionStatus = async (
    status,
    label,
    { pushFirebase = false, force = false, keepBlocked = false } = {},
  ) => {
    if (!stock?.id || !selectedSeatId || assigningSel) return;
    // Sifre ONCE — yoksa ne Postgres ne Firebase
    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli) — Postgres/Firebase yazılmadı', { type: 'warning' });
      return;
    }
    setAssigningSel(true);
    if (pushFirebase) setPushing(true);
    try {
      const map = normalizeSeatSelections(seatSelections);
      const ticket = selectedTickets[0];
      const prev = map[selectedSeatId];
      const nextEntry = {
        status,
        statusChangedAt: String(Math.floor(Date.now() / 1000)),
        user:
          status === 0
            ? null
            : ticket?.user?.id ||
              ticket?.user ||
              prev?.user ||
              null,
      };
      if (status === 0) {
        nextEntry.isBlockedSeat = false;
      } else if (keepBlocked || prev?.isBlockedSeat === true) {
        nextEntry.isBlockedSeat = true;
      } else if (force) {
        // zorla 1: blok flag yoksa ekleme (sadece kilit)
        nextEntry.isBlockedSeat = false;
      }
      const next = {
        ...map,
        [selectedSeatId]: nextEntry,
      };
      await dataProvider.update('seatSelections', { id: stock.id, data: next });
      notify(`${selectedSeatId}: Postgres seatSelections → ${label}`, { type: 'success' });
      await loadAll();

      if (pushFirebase) {
        await pushSeatLiveStateToFirebase({
          eventId: eventID,
          stockId: stock.id,
          seatId: selectedSeatId,
          dataProvider,
        });
        notify(`Firebase seatSelections push OK: ${selectedSeatId}`, { type: 'success' });
      }
    } catch (err) {
      notify(`seatSelections güncellenemedi: ${err?.message || err}`, { type: 'error' });
    } finally {
      setAssigningSel(false);
      if (pushFirebase) setPushing(false);
    }
  };

  const handleMarkSold = async () => {
    if (!selectedSeatId) return;
    if (selectedTickets.length === 0) {
      notify('Bu koltukta bilet yok — önce bilet olmalı veya koltuk ata', { type: 'warning' });
      return;
    }
    const ok = window.confirm(
      `${selectedSeatId} seatSelections = 1 (satılmış)\nPostgres + Firebase seatSelections.\nŞifre sorulacak — yoksa yazılmaz.\nDevam?`,
    );
    if (!ok) return;
    await updateSeatSelectionStatus(1, 'satılmış (1)', { pushFirebase: true });
  };

  const handleForceSeatSelOne = async () => {
    if (!selectedSeatId) return;
    const ok = window.confirm(
      `⚠️ ADMIN ONLY — ZORLA seatSelections = 1\n\n` +
        `Koltuk: ${selectedSeatId}\n` +
        `Bilet şartı YOK (anlık veri gelmese de kilitlenir).\n` +
        `Postgres + Firebase yazılır.\n` +
        `Yanlış kullanım çift satış / yanlış kilit riski.\n\n` +
        `Sadece admin kullansın. Devam?`,
    );
    if (!ok) return;
    await updateSeatSelectionStatus(1, 'zorla 1 (admin)', {
      pushFirebase: true,
      force: true,
      keepBlocked: selectedSeatSel?.isBlockedSeat === true,
    });
  };

  /**
   * Admin rezerv flag'ini kaldir; status 1 kalsin (kapali).
   * Postgres + Firebase seatSelections + stocks.seatSelections.
   */
  const clearAdminReserveOnSeat = async (seatId) => {
    if (!stock?.id || !seatId || assigningSel) return false;
    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli) — Postgres/Firebase yazılmadı', { type: 'warning' });
      return false;
    }
    setAssigningSel(true);
    setPushing(true);
    try {
      const map = normalizeSeatSelections(seatSelections);
      const prev = map[seatId] || {};
      const nextEntry = {
        ...prev,
        status: 1,
        isBlockedSeat: false,
        statusChangedAt: String(Math.floor(Date.now() / 1000)),
        user: prev.user ?? null,
      };
      await dataProvider.update('seatSelections', {
        id: stock.id,
        data: { ...map, [seatId]: nextEntry },
      });
      await pushSeatLiveStateToFirebase({
        eventId: eventID,
        stockId: stock.id,
        seatId,
        dataProvider,
      });
      notify(
        `${seatId}: admin rezerv silindi · status 1 kaldı (PG + Firebase)`,
        { type: 'success' },
      );
      await loadAll();
      return true;
    } catch (err) {
      notify(`Admin rezerv silinemedi: ${err?.message || err}`, { type: 'error' });
      return false;
    } finally {
      setAssigningSel(false);
      setPushing(false);
    }
  };

  const handleClearAdminReserve = async (seatId) => {
    const id = seatId || selectedSeatId;
    if (!id) return;
    const ok = window.confirm(
      `${id}: Admin rezervasyonu silinsin mi?\n\n` +
        `isBlockedSeat → false\n` +
        `status → 1 (kapalı kalır, web satmaz)\n` +
        `Postgres + Firebase.\nŞifre sorulacak.\nDevam?`,
    );
    if (!ok) return;
    await clearAdminReserveOnSeat(id);
  };

  const handleMarkEmpty = async () => {
    if (!selectedSeatId) return;
    if (selectedTickets.length > 0) {
      const okRisk = window.confirm(
        `DİKKAT: ${selectedSeatId} için hâlâ bilet var.\nBoş (0) yaparsan web tekrar satabilir → çift satış riski.\nYine de devam?`,
      );
      if (!okRisk) return;
    }
    const ok = window.confirm(
      `${selectedSeatId} seatSelections = 0 (boş)\nPostgres + Firebase seatSelections.\nŞifre sorulacak — yoksa yazılmaz.\nDevam?`,
    );
    if (!ok) return;
    await updateSeatSelectionStatus(0, 'boş (0)', { pushFirebase: true });
  };

  const handleClearHold = async () => {
    if (!stock?.id || !selectedSeatId || clearingHold || assigningSel) return;
    const ok = window.confirm(
      `${selectedSeatId} hold temizlenecek (seatSelections → 0)\nPostgres + Firebase.\nŞifre sorulacak.\nDevam?`,
    );
    if (!ok) return;
    setClearingHold(true);
    try {
      await updateSeatSelectionStatus(0, 'boş (0) — hold temiz', { pushFirebase: true });
    } finally {
      setClearingHold(false);
    }
  };

  const handleCreateMissingTicket = async () => {
    if (!event || !stock?.id || !selectedSeatId || creatingTicket) return;
    if (selectedTickets.length > 0) {
      notify('Bu koltukta zaten bilet var', { type: 'info' });
      return;
    }

    const match = saleMatches[0] || null;
    const isBlocked = selectedSeatSel?.isBlockedSeat === true;

    // Bloklu koltuk + sale yok = gerçek alıcı yok. Admin user'a bilet yazma.
    if (isBlocked && !match) {
      notify(
        `${selectedSeatId}: isBlockedSeat — bu satış değil, admin blok. seatSelections.user alıcı değil. Bilet oluşturma. Önce Boşalt (0) veya gerçek sale/order bul.`,
        { type: 'warning' },
      );
      return;
    }

    if (!match && !selectedSeatSel?.user) {
      notify('Ne sale ne user var — kime bilet yazılacağı belli değil', { type: 'warning' });
      return;
    }

    // Sale yoksa user admin/blocked ise yine dur
    if (!match && seatSelUser && (seatSelUser.role === 'admin' || seatSelUser.crole === 'admin')) {
      notify(
        `${selectedSeatId}: seatSelections.user admin (${formatUserFullName(seatSelUser)}). Gerçek alıcı değil — bilet oluşturulmaz.`,
        { type: 'warning' },
      );
      return;
    }

    const userId = match?.user?.id || match?.user || selectedSeatSel?.user || null;
    const meta = layoutSeatMeta.get(selectedSeatId);
    const productId = meta?.productId || null;
    const product = products.find((p) => String(p.id) === String(productId)) || null;

    const buyerLabel = match
      ? formatUserFullName(match.user) || match.user?.email || match.user?.id || '—'
      : formatUserFullName(seatSelUser) || seatSelUser?.email || String(userId);

    const ok = window.confirm(
      match
        ? `${selectedSeatId} için bilet → alıcı: ${buyerLabel}\nSale: ${match.saleId}\nOrder/OID: ${match.orderId || match.oid || '—'}\nPostgres only. Devam?`
        : `${selectedSeatId} için SALE YOK.\nBilet şu user’a yazılacak:\n${buyerLabel}\n(${userId})\nEmin misin? (Postgres only)`,
    );
    if (!ok) return;

    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli) — bilet oluşturulmadı', { type: 'warning' });
      return;
    }

    setCreatingTicket(true);
    try {
      let userObj = match?.user || seatSelUser || null;
      if (userObj?._missing) userObj = { id: String(userId) };
      if ((!userObj || typeof userObj !== 'object') && userId) {
        try {
          const { data: u } = await dataProvider.getOne('users', { id: String(userId) });
          userObj = u;
        } catch {
          userObj = { id: String(userId) };
        }
      }

      const ticketId = generateTicketID();
      const template = match?.ticketTemplate || null;
      const templateSeat = getTicketSeats(template).find(
        (s) => String(s.id) === String(selectedSeatId),
      );

      const seatRow = {
        id: selectedSeatId,
        productID: productId || templateSeat?.productID || template?.product?.id || null,
        selectedOption: templateSeat?.selectedOption ||
          (product
            ? {
                price: product.price,
                title: product.title,
                isPromotionSelected: false,
              }
            : null),
      };

      const price =
        typeof template?.price === 'number'
          ? template.price
          : typeof product?.price === 'number'
            ? product.price
            : 0;

      const ticketPayload = {
        id: ticketId,
        pnr: ticketId,
        price,
        channel: match?.channel || template?.channel || 'op',
        isUsed: false,
        sale: match?.saleId || null,
        venue: event.venue || null,
        product: product
          ? { id: product.id, title: product.title, price: product.price, key: product.key }
          : template?.product || null,
        event: {
          id: event.id,
          name: event.name,
          stockID: stock.id,
          type: event.type,
          venue: event.venue || null,
        },
        user: userObj,
        created: Date.now(),
        seats: [seatRow],
        extras: {
          seats: [seatRow],
          createdFromSeatOps: true,
          sourceSaleId: match?.saleId || null,
          sourceOrderId: match?.orderId || match?.oid || null,
          isBlockedSeat: selectedSeatSel?.isBlockedSeat === true,
        },
      };

      await dataProvider.update('tickets', { id: ticketId, data: ticketPayload });
      notify(
        `Bilet oluşturuldu: ${ticketId} → ${buyerLabel} (Postgres). Firebase için “seatSelections → Firebase” kullan.`,
        { type: 'success' },
      );
      await loadAll();
    } catch (err) {
      console.error(err);
      notify(`Bilet oluşturulamadı: ${err?.message || err}`, { type: 'error' });
    } finally {
      setCreatingTicket(false);
    }
  };

  /** Bos veya bloklu koltuga: user id ile sale + ticket + seatSelections=1
   *  Ayni user'in bu eventte iade edilmemis bileti varsa YENI bilet acilmaz —
   *  mevcut bilete koltuk eklenir / guncellenir.
   */
  const handleAssignSaleByUserId = async () => {
    if (!event || !stock?.id || !selectedSeatId || assigningSale) return;
    if (selectedTickets.length > 0) {
      notify('Bu koltukta zaten bilet var', { type: 'warning' });
      return;
    }

    const uid = String(manualUserId || '').trim();
    if (!uid) {
      notify('User ID gir', { type: 'warning' });
      return;
    }

    let userObj = manualUserPreview;
    if (!userObj || userObj._missing || String(userObj.id) !== uid) {
      try {
        const { data: u } = await dataProvider.getOne('users', { id: uid });
        userObj = u;
      } catch {
        notify(`User bulunamadı: ${uid}`, { type: 'error' });
        return;
      }
    }

    const meta = layoutSeatMeta.get(selectedSeatId);
    const productId = meta?.productId || null;
    if (!productId) {
      notify(`${selectedSeatId}: layout'ta ürün (ticket) atanmamış — satış yapılamaz`, {
        type: 'error',
      });
      return;
    }
    const product = products.find((p) => String(p.id) === String(productId));
    if (!product) {
      notify(`Ürün stock'ta yok: ${productId}`, { type: 'error' });
      return;
    }

    const price = typeof product.price === 'number' ? product.price : 0;
    const buyerLabel = formatUserFullName(userObj) || userObj.email || uid;
    const wasBlocked = selectedSeatSel?.isBlockedSeat === true;

    // Ayni user + event → mevcut bilet (iade degil)
    const existingTicket = (tickets || []).find((t) => {
      if (isTicketRefunded(t)) return false;
      const tuid = t.user?.id || (typeof t.user === 'string' ? t.user : null);
      return String(tuid) === String(uid);
    });

    if (existingTicket) {
      const okMove = window.confirm(
        `${selectedSeatId} → mevcut bilete eklenecek (YENİ bilet YOK)\n` +
          `Alıcı: ${buyerLabel}\n` +
          `Mevcut PNR: ${existingTicket.pnr || existingTicket.id}\n` +
          `Ürün: ${product.title?.tr || product.title?.en || productId} · ${price}\n` +
          `${wasBlocked ? 'Blok kaldırılacak.\n' : ''}` +
          `Postgres + Firebase (bilet + seatSelections).\nŞifre sorulacak.\nDevam?`,
      );
      if (!okMove) return;
      if (!(await requirePushPassword())) {
        notify('İptal (şifre gerekli) — yazılmadı', { type: 'warning' });
        return;
      }

      setAssigningSale(true);
      setPushing(true);
      try {
        const nowSec = String(Math.floor(Date.now() / 1000));
        const seatRow = {
          id: selectedSeatId,
          productID: productId,
          selectedOption: {
            price,
            title: product.title,
            isPromotionSelected: false,
          },
        };
        const oldSeats = getTicketSeats(existingTicket);
        const withoutDup = oldSeats.filter((s) => String(s.id) !== String(selectedSeatId));
        const nextSeats = [...withoutDup, seatRow];

        await dataProvider.update('tickets', {
          id: existingTicket.id,
          data: {
            ...existingTicket,
            seats: nextSeats,
            extras: {
              ...(existingTicket.extras && typeof existingTicket.extras === 'object'
                ? existingTicket.extras
                : {}),
              seats: nextSeats,
              refunded: false,
            },
          },
        });

        const map = normalizeSeatSelections(seatSelections);
        const nextSel = {
          ...map,
          [selectedSeatId]: {
            status: 1,
            statusChangedAt: nowSec,
            user: uid,
            isBlockedSeat: false,
          },
        };
        await dataProvider.update('seatSelections', { id: stock.id, data: nextSel });

        await pushTicketAndSeatsToFirebase({
          eventId: eventID,
          stockId: stock.id,
          ticketId: existingTicket.id,
          seatIds: [selectedSeatId],
          dataProvider,
        });

        notify(
          `Mevcut bilet + Firebase OK: ${existingTicket.pnr || existingTicket.id} ← ${selectedSeatId}`,
          { type: 'success' },
        );
        setManualUserId('');
        setManualUserPreview(null);
        await loadAll();
      } catch (err) {
        console.error(err);
        notify(`Koltuk eklenemedi: ${err?.message || err}`, { type: 'error' });
      } finally {
        setAssigningSale(false);
        setPushing(false);
      }
      return;
    }

    const ok = window.confirm(
      `${selectedSeatId} → satış + bilet oluşturulacak (Postgres)\n` +
        `Alıcı: ${buyerLabel}\nUser ID: ${uid}\n` +
        `Ürün: ${product.title?.tr || product.title?.en || productId} · ${price}\n` +
        `${wasBlocked ? 'Blok kaldırılıp bu user’a atanacak.\n' : ''}` +
        `Şifre sorulacak. Firebase ayrı push.\nDevam?`,
    );
    if (!ok) return;

    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli) — yazılmadı', { type: 'warning' });
      return;
    }

    setAssigningSale(true);
    try {
      const saleId = generateSaleDocId();
      const ticketId = generateSaleDocId();
      const pnr = `${Date.now()}${Math.floor(Math.random() * 1000)
        .toString()
        .padStart(3, '0')}`;
      const nowMs = Date.now();
      const nowSec = String(Math.floor(nowMs / 1000));

      const seatRow = {
        id: selectedSeatId,
        productID: productId,
        selectedOption: {
          price,
          title: product.title,
          isPromotionSelected: false,
        },
      };

      const eventRef = buildEventRefForTicket();
      const userPayload = {
        id: userObj.id || uid,
        name: userObj.name || '',
        surname: userObj.surname || '',
        email: userObj.email || '',
        mobile: userObj.mobile || '',
        gender: userObj.gender || '',
        city: userObj.city || '',
        createdate: userObj.createdate || null,
      };

      const ticketPayload = {
        id: ticketId,
        pnr,
        price,
        channel: 'op',
        isUsed: false,
        sale: saleId,
        venue: event.venue || null,
        isUsedBy: null,
        isUsedAt: null,
        product: {
          id: productId,
          title: product.title,
          price,
        },
        event: eventRef,
        user: userPayload,
        created: nowMs,
        extras: {
          seats: [seatRow],
          createdFromSeatOps: true,
        },
        seats: [seatRow],
      };

      const saleTicketSnap = {
        id: ticketId,
        pnr,
        price,
        seats: [seatRow],
        product: ticketPayload.product,
        event: eventRef,
        user: userPayload,
      };

      const salePayload = {
        id: saleId,
        status: 1,
        channel: 'op',
        paymentType: 'comp',
        paymentTotal: price,
        createdateMs: nowMs,
        createdate: nowMs,
        oid: null,
        user: userPayload,
        payment: null,
        event: eventRef,
        details: { seats: [seatRow], note: 'seat-ops manual assign' },
        tickets: [saleTicketSnap],
        extras: {
          createdFromSeatOps: true,
          seatId: selectedSeatId,
        },
      };

      await dataProvider.update('sales', { id: saleId, data: salePayload });
      await dataProvider.update('tickets', { id: ticketId, data: ticketPayload });

      const map = normalizeSeatSelections(seatSelections);
      const nextSel = {
        ...map,
        [selectedSeatId]: {
          status: 1,
          statusChangedAt: nowSec,
          user: uid,
          isBlockedSeat: false,
        },
      };
      await dataProvider.update('seatSelections', { id: stock.id, data: nextSel });

      setPushing(true);
      await pushTicketAndSeatsToFirebase({
        eventId: eventID,
        stockId: stock.id,
        ticketId,
        seatIds: [selectedSeatId],
        dataProvider,
      });

      notify(
        `Satış + Firebase OK: ${selectedSeatId} → ${buyerLabel} · Ticket ${pnr}`,
        { type: 'success' },
      );
      setManualUserId('');
      setManualUserPreview(null);
      await loadAll();
    } catch (err) {
      console.error(err);
      notify(`Satış oluşturulamadı: ${err?.message || err}`, { type: 'error' });
    } finally {
      setAssigningSale(false);
      setPushing(false);
    }
  };

  /**
   * Cakisma / fazla kisi: bu bileti (veya bu koltugu) iade et.
   * Kisi duserer; seatSelections=1 KALIR (diger kisi/Firebase kilit icin bosaltma).
   * Sadece Postgres; seatSelections → Firebase ayri buton.
   */
  const handleRefundTicketOnSeat = async (ticket) => {
    if (!stock?.id || !selectedSeatId || !ticket?.id || refundingTicketId) return;

    const label = formatUserFullName(ticket.user) || ticket.pnr || ticket.id;
    const ok = window.confirm(
      `İADE\n\n` +
        `Kişi: ${label}\n` +
        `PNR: ${ticket.pnr || ticket.id}\n` +
        `Koltuk: ${selectedSeatId} bu bileten çıkarılacak.\n` +
        `Koltuk boşalmaz → seatSelections = 1 kalır (diğer alıcı / Firebase kilit).\n` +
        `Postgres + Firebase (seatSelections + stocks).\n\nDevam?`,
    );
    if (!ok) return;
    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli)', { type: 'warning' });
      return;
    }

    setRefundingTicketId(ticket.id);
    setPushing(true);
    try {
      const oldSeats = getTicketSeats(ticket);
      const nextSeats = oldSeats.filter((s) => String(s.id) !== String(selectedSeatId));
      const fullyRefunded = nextSeats.length === 0;
      const extras = {
        ...(ticket.extras && typeof ticket.extras === 'object' ? ticket.extras : {}),
        seats: nextSeats,
      };
      if (fullyRefunded) {
        extras.refunded = true;
        extras.refundedAt = Date.now();
        extras.refundedFromSeatOps = true;
        extras.refundedSeat = selectedSeatId;
      }

      await dataProvider.update('tickets', {
        id: ticket.id,
        data: {
          ...ticket,
          seats: nextSeats,
          extras,
        },
      });

      // Sale: tum biletler iade ise status 3
      const saleId = ticket.sale?.id || (typeof ticket.sale === 'string' ? ticket.sale : null);
      if (saleId && fullyRefunded) {
        try {
          const { data: sale } = await dataProvider.getOne('sales', { id: saleId });
          const saleTickets = Array.isArray(sale?.tickets) ? sale.tickets : [];
          const remainingRefs = saleTickets.filter((t) => String(t.id || t) !== String(ticket.id));
          // Diger biletler de iade mi?
          let allRefunded = remainingRefs.length === 0;
          if (!allRefunded) {
            const others = await Promise.all(
              remainingRefs.map(async (ref) => {
                const tid = ref.id || ref;
                try {
                  const { data: ot } = await dataProvider.getOne('tickets', { id: tid });
                  return isTicketRefunded(ot);
                } catch {
                  return true;
                }
              }),
            );
            allRefunded = others.every(Boolean);
          }
          if (allRefunded) {
            await dataProvider.update('sales', {
              id: saleId,
              data: {
                ...sale,
                status: 3,
                extras: {
                  ...(sale.extras && typeof sale.extras === 'object' ? sale.extras : {}),
                  refunded: true,
                  refundedAt: Date.now(),
                  refundedFromSeatOps: true,
                },
              },
            });
          }
        } catch (e) {
          console.warn('sale iade guncelleme atlandi', e);
        }
      }

      // seatSelections: BOSALTMA — 1 kalsin, user = kalan bilet sahibi
      const remainingOnSeat = (analysis.seatsByTicket.get(selectedSeatId) || []).filter(
        (t) => t.id !== ticket.id && !isTicketRefunded(t),
      );
      const map = normalizeSeatSelections(seatSelections);
      const keepUser =
        remainingOnSeat[0]?.user?.id ||
        remainingOnSeat[0]?.user ||
        map[selectedSeatId]?.user ||
        null;
      const nextSel = {
        ...map,
        [selectedSeatId]: {
          status: 1,
          statusChangedAt: String(Math.floor(Date.now() / 1000)),
          user: keepUser,
          ...(map[selectedSeatId]?.isBlockedSeat
            ? { isBlockedSeat: map[selectedSeatId].isBlockedSeat }
            : { isBlockedSeat: false }),
        },
      };
      await dataProvider.update('seatSelections', { id: stock.id, data: nextSel });

      await pushSeatLiveStateToFirebase({
        eventId: eventID,
        stockId: stock.id,
        seatId: selectedSeatId,
        dataProvider,
      });

      notify(
        fullyRefunded
          ? `İade + Firebase OK: ${label} · bilet tamamen iade · ${selectedSeatId} hâlâ 1`
          : `İade + Firebase OK: ${label} · ${selectedSeatId} bileten çıktı · hâlâ 1`,
        { type: 'success' },
      );
      await loadAll();
    } catch (err) {
      console.error(err);
      notify(`İade hata: ${err?.message || err}`, { type: 'error' });
    } finally {
      setRefundingTicketId(null);
      setPushing(false);
    }
  };

  const handleChangeSeat = async (ticket) => {
    if (!stock?.id || !selectedSeatId || savingSeat) return;
    const newSeatId = String(seatEdits[ticket.id] || '').trim().toUpperCase();
    if (!newSeatId) {
      notify('Yeni koltuk kodu gir', { type: 'warning' });
      return;
    }
    if (newSeatId === selectedSeatId) {
      notify('Aynı koltuk', { type: 'info' });
      return;
    }
    if (!layoutSeatMeta.has(newSeatId)) {
      notify(`Layout'ta koltuk yok: ${newSeatId}`, { type: 'error' });
      return;
    }
    const occupied = analysis.seatsByTicket.get(newSeatId) || [];
    if (occupied.some((t) => t.id !== ticket.id)) {
      notify(`Hedef koltuk dolu: ${newSeatId}`, { type: 'error' });
      return;
    }

    const remainingOnOld = (analysis.seatsByTicket.get(selectedSeatId) || []).filter(
      (t) => t.id !== ticket.id && !isTicketRefunded(t),
    );
    const oldSeatNote =
      remainingOnOld.length > 0
        ? `Eski ${selectedSeatId}: bu kişiden iade → hâlâ 1 (kalan ${remainingOnOld.length} kişi)`
        : `Eski ${selectedSeatId}: bu kişiden iade → kimse kalmadı, 0 (boş)`;

    const ok = window.confirm(
      `Değiştir = eski koltuk iade + yeni koltuk ata\n\n` +
        `Bilet ${ticket.pnr || ticket.id}: ${selectedSeatId} → ${newSeatId}\n` +
        `${oldSeatNote}\n` +
        `Yeni ${newSeatId}: seatSelections = 1\n\n` +
        `Postgres + Firebase.\nŞifre sorulacak.\nDevam?`,
    );
    if (!ok) return;

    if (!(await requirePushPassword())) {
      notify('İptal (şifre gerekli) — Postgres/Firebase yazılmadı', { type: 'warning' });
      return;
    }

    setSavingSeat(ticket.id);
    setPushing(true);
    try {
      const oldSeats = getTicketSeats(ticket);
      const seatRow = oldSeats.find((s) => String(s.id) === String(selectedSeatId));
      const newMeta = layoutSeatMeta.get(newSeatId);
      const productId = newMeta?.productId || seatRow?.productID;
      const product = products.find((p) => String(p.id) === String(productId));

      const nextSeats = oldSeats.map((s) => {
        if (String(s.id) !== String(selectedSeatId)) return s;
        return {
          ...s,
          id: newSeatId,
          productID: productId || s.productID,
          selectedOption: product
            ? {
                price: product.price,
                title: product.title,
                isPromotionSelected: false,
              }
            : s.selectedOption,
        };
      });

      await dataProvider.update('tickets', {
        id: ticket.id,
        data: {
          ...ticket,
          seats: nextSeats,
          extras: {
            ...(ticket.extras && typeof ticket.extras === 'object' ? ticket.extras : {}),
            seats: nextSeats,
            seatChangedFrom: selectedSeatId,
            seatChangedTo: newSeatId,
            seatChangedAt: Date.now(),
            seatChangedFromSeatOps: true,
          },
        },
      });

      const now = String(Math.floor(Date.now() / 1000));
      const map = normalizeSeatSelections(seatSelections);

      // Eski koltuk = iade mantigi
      let oldSeatEntry;
      if (remainingOnOld.length > 0) {
        const keepUser =
          remainingOnOld[0]?.user?.id ||
          remainingOnOld[0]?.user ||
          map[selectedSeatId]?.user ||
          null;
        oldSeatEntry = {
          status: 1,
          statusChangedAt: now,
          user: keepUser,
          ...(map[selectedSeatId]?.isBlockedSeat
            ? { isBlockedSeat: map[selectedSeatId].isBlockedSeat }
            : { isBlockedSeat: false }),
        };
      } else {
        oldSeatEntry = {
          status: 0,
          statusChangedAt: now,
          user: null,
          isBlockedSeat: false,
        };
      }

      const nextSel = {
        ...map,
        [selectedSeatId]: oldSeatEntry,
        [newSeatId]: {
          status: 1,
          statusChangedAt: now,
          user: ticket.user?.id || ticket.user || null,
          isBlockedSeat: false,
        },
      };
      await dataProvider.update('seatSelections', { id: stock.id, data: nextSel });

      await pushTicketAndSeatsToFirebase({
        eventId: eventID,
        stockId: stock.id,
        ticketId: ticket.id,
        seatIds: [selectedSeatId, newSeatId],
        dataProvider,
      });

      const fromSeat = selectedSeatId;
      notify(
        remainingOnOld.length > 0
          ? `Değiştir OK: ${fromSeat}→${newSeatId} · eski iade (1 kaldı) · Firebase yazıldı`
          : `Değiştir OK: ${fromSeat}→${newSeatId} · eski iade (0 boş) · Firebase yazıldı`,
        { type: 'success' },
      );
      setSeatEdits((prev) => {
        const n = { ...prev };
        delete n[ticket.id];
        return n;
      });
      setSelectedSeatId(newSeatId);
      await loadAll();
    } catch (err) {
      console.error(err);
      notify(`Koltuk değişmedi: ${err?.message || err}`, { type: 'error' });
    } finally {
      setSavingSeat(null);
      setPushing(false);
    }
  };

  if (!eventID) {
    return (
      <Box p={3}>
        <Alert severity="error">eventID eksik. /seat-ops?eventID=...</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={4} display="flex" alignItems="center" gap={2}>
        <CircularProgress size={28} />
        <Typography>Koltuk ops yükleniyor…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={loadAll} startIcon={<RefreshIcon />}>
          Tekrar dene
        </Button>
      </Box>
    );
  }

  return (
    <Box p={2}>
      {PushPasswordDialog}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Box>
          <Typography variant="h5">{event?.name || eventID}</Typography>
          <Typography variant="body2" color="text.secondary">
            Koltuk Ops · {eventID}
            {pulledAt ? ` · Son pull: ${new Date(pulledAt).toLocaleString()}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAll}
            disabled={loading || pulling || pushing}
          >
            Yenile
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={() => {
              try {
                exportSeatOpsExcel({
                  event,
                  eventID,
                  tickets,
                  analysis,
                });
                notify('Çakışma Excel raporu indirildi', { type: 'success' });
              } catch (err) {
                console.error(err);
                notify(`${err?.message || err}`, { type: 'warning' });
              }
            }}
            disabled={loading || !(analysis?.conflicts?.length > 0)}
          >
            Çakışma Excel
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<ConfirmationNumberOutlinedIcon />}
            onClick={handleFixTicketPrint}
            disabled={loading || pulling || pushing || fixingTicketPrint || !eventID}
          >
            {fixingTicketPrint ? 'Düzeltiliyor…' : 'Biletleri aç'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<CloudDownloadIcon />}
            onClick={handlePull}
            disabled={pulling || pushing || bulkFilling}
          >
            {pulling ? 'Çekiliyor…' : 'Son hali çek (REPLACE)'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PlaylistAddCheckIcon />}
            onClick={handleBulkFillSoldFlags}
            disabled={
              loading ||
              pulling ||
              pushing ||
              bulkFilling ||
              !stock?.id ||
              (!missingSoldFlagCount && !stockFbMismatchCount)
            }
          >
            {bulkFilling
              ? 'Toplu yazılıyor…'
              : `Toplu düzelt (${missingSoldFlagCount + stockFbMismatchCount})`}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSyncStockFbMismatches}
            disabled={
              loading || pulling || pushing || bulkFilling || !stock?.id || !stockFbMismatchCount
            }
          >
            {`2. yer sync (${stockFbMismatchCount})`}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CloudUploadIcon />}
            onClick={handlePush}
            disabled={pulling || pushing || bulkFilling}
          >
            {pushing && !bulkFilling ? 'Push…' : 'Firebase’e push (şifreli)'}
          </Button>
        </Stack>
      </Stack>

      {analysis.conflicts.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {analysis.conflicts.length} uyarı / çakışma bulundu. Sağ listeden seçip müdahale edebilirsin.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="flex-start">
        <Box>
          {layoutJSON ? (
            <SeatOpsViewer
              layoutJSON={layoutJSON}
              soldSeatIds={analysis.soldSeatIds}
              holdSeatIds={analysis.holdSeatIds}
              blockedSeatIds={analysis.blockedSeatIds}
              conflictSeatIds={analysis.conflictSeatIds}
              syncSeatIds={analysis.stockFbMismatchSeatIds || []}
              selectedSeatId={selectedSeatId}
              onSeatClick={({ seatId }) => setSelectedSeatId(seatId)}
            />
          ) : (
            <Alert severity="warning">Event layout bulunamadı (layoutJSON boş).</Alert>
          )}
          <Stack direction="row" gap={1} mt={1} flexWrap="wrap">
            <Chip size="small" label="Boş / ürün rengi" />
            <Chip size="small" sx={{ bgcolor: '#374151', color: '#fff' }} label="Satılmış" />
            <Chip
              size="small"
              sx={{
                bgcolor: '#ffedd5',
                color: '#9a3412',
                border: '2px solid #c2410c',
              }}
              label="Admin rezerv"
            />
            <Chip size="small" sx={{ bgcolor: '#f59e0b' }} label="Hold" />
            <Chip size="small" sx={{ bgcolor: '#dc2626', color: '#fff' }} label="Çakışma" />
            <Chip size="small" sx={{ bgcolor: '#2563eb', color: '#fff' }} label="2. yer sync" />
          </Stack>
        </Box>

        <Box
          sx={{
            minWidth: 300,
            maxWidth: 420,
            flex: 1,
            border: '1px solid #e2e8f0',
            borderRadius: 1,
            p: 2,
            maxHeight: '75vh',
            overflow: 'auto',
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Ürünler
          </Typography>
          <Stack gap={0.5} mb={2}>
            {products.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Ürün yok
              </Typography>
            )}
            {products.map((p) => (
              <Stack key={p.id} direction="row" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    bgcolor: productColour(p.id),
                    border: '1px solid #333',
                  }}
                />
                <Typography variant="body2">
                  {p.title?.tr || p.title?.en || p.id} · {p.price} · sold:{p.sold ?? 0}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle1" gutterBottom>
            Admin rezerv ({analysis.blockedSeatIds?.length || 0})
            {adminBlockedWithTicketSeatIds.length > 0
              ? ` · ${adminBlockedWithTicketSeatIds.length} tanesinde bilet var`
              : ''}
          </Typography>
          {adminBlockedWithTicketSeatIds.length > 0 && (
            <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
              <Typography variant="caption" display="block" fontWeight={700}>
                Bilet + admin rezerv çakışması ({adminBlockedWithTicketSeatIds.length}):
              </Typography>
              <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                {adminBlockedWithTicketSeatIds.join(', ')}
              </Typography>
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            PG + Firebase stocks. Biletli olanlar da listelenir. Sil = isBlockedSeat kaldır, status 1
            kalsın.
          </Typography>
          <List dense disablePadding sx={{ mb: 1, maxHeight: 280, overflow: 'auto' }}>
            {(analysis.blockedSeatIds || []).length === 0 && (
              <ListItem>
                <ListItemText secondary="Admin rezerv yok" />
              </ListItem>
            )}
            {[...(analysis.blockedSeatIds || [])]
              .slice()
              .sort((a, b) => {
                const aT = analysis.seatsByTicket?.get?.(a)?.length || 0;
                const bT = analysis.seatsByTicket?.get?.(b)?.length || 0;
                if (aT !== bT) return bT - aT; // biletli once
                return String(a).localeCompare(String(b), undefined, { numeric: true });
              })
              .map((seatId) => {
                const ticketsOnSeat = analysis.seatsByTicket?.get?.(seatId) || [];
                const hasTicket = ticketsOnSeat.length > 0;
                const pnrLabel = hasTicket
                  ? ticketsOnSeat
                      .map((t) => t.pnr || t.id)
                      .filter(Boolean)
                      .join(', ')
                  : '';
                return (
                  <ListItem
                    key={`blocked-${seatId}`}
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        color="warning"
                        title="Admin rezervasyonu sil"
                        aria-label={`${seatId} admin rezervasyonu sil`}
                        disabled={assigningSel || pushing || loading}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearAdminReserve(seatId);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      onClick={() => setSelectedSeatId(seatId)}
                      selected={selectedSeatId === seatId}
                      sx={{ pr: 6 }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" gap={0.75}>
                            <span>{seatId}</span>
                            {hasTicket && (
                              <Chip
                                size="small"
                                color="error"
                                label={`Bilet ×${ticketsOnSeat.length}`}
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            )}
                          </Stack>
                        }
                        secondary={
                          hasTicket
                            ? `Admin rezerv + bilet var · ${pnrLabel}`
                            : 'isBlockedSeat — admin rezerv / kapalı'
                        }
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: hasTicket ? '#b91c1c' : '#c2410c',
                          component: 'div',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
          </List>

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle1" gutterBottom>
            Çakışmalar ({analysis.conflicts.length})
          </Typography>
          <List dense disablePadding>
            {analysis.conflicts.length === 0 && (
              <ListItem>
                <ListItemText secondary="Çakışma yok" />
              </ListItem>
            )}
            {analysis.conflicts.map((c, idx) => {
              const isSync =
                c.type === 'stock_fb_missing' ||
                c.type === 'stock_fb_orphan' ||
                c.type === 'stock_fb_mismatch';
              const isOverlap = c.type === 'overlap' || c.type === 'missing_layout';
              return (
                <ListItemButton
                  key={`${c.type}-${c.seatId}-${idx}`}
                  onClick={() => setSelectedSeatId(c.seatId)}
                  selected={selectedSeatId === c.seatId}
                >
                  <ListItemText
                    primary={c.seatId}
                    secondary={c.message}
                    primaryTypographyProps={{
                      color: isSync ? '#2563eb' : isOverlap ? 'error' : 'warning.main',
                      fontWeight: 600,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle1" gutterBottom>
            Seçili koltuk
          </Typography>
          {!selectedSeatId ? (
            <Typography variant="body2" color="text.secondary">
              Layout’tan bir koltuk seç
            </Typography>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedSeatId}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                seatSelections:{' '}
                {selectedSeatSel?.isBlockedSeat
                  ? `${selectedSelStatus ?? '?'} admin rezerv (isBlockedSeat)`
                  : selectedSelStatus === 1
                    ? '1 satılmış'
                    : selectedSelStatus === 2
                      ? '2 hold'
                      : selectedSelStatus === 0
                        ? '0 boş'
                        : 'yok'}
                {selectedTickets.length > 0 ? ` · ${selectedTickets.length} bilet` : ''}
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap" mb={1}>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  disabled={assigningSel || pushing || selectedSelStatus === 1}
                  onClick={handleMarkSold}
                >
                  Satılmış yap (1)
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    bgcolor: '#ffedd5',
                    color: '#9a3412',
                    border: '2px solid #c2410c',
                    '&:hover': { bgcolor: '#fed7aa' },
                  }}
                  disabled={assigningSel || pushing}
                  onClick={handleForceSeatSelOne}
                >
                  Zorla seatSel = 1
                </Button>
                {selectedSeatSel?.isBlockedSeat === true && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<DeleteOutlineIcon />}
                    disabled={assigningSel || pushing}
                    onClick={() => handleClearAdminReserve(selectedSeatId)}
                  >
                    Admin rezervasyonu sil
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  disabled={assigningSel || pushing || selectedSelStatus === 0}
                  onClick={handleMarkEmpty}
                >
                  Boşalt (0)
                </Button>
              </Stack>
              <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
                <Typography variant="caption" display="block">
                  <strong>Sadece admin:</strong> “Zorla seatSel = 1” bilet/pull beklemeden
                  koltuğu kilitler. Başkası dokunmasın — yanlış kullanımda plan kilitlenir /
                  satış bozulabilir.
                </Typography>
              </Alert>
              <Stack direction="row" gap={1} flexWrap="wrap" mb={1.5}>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<CloudUploadIcon />}
                  disabled={!stock?.id || pushing || assigningSel}
                  onClick={handlePushSelectedSeat}
                >
                  {pushing ? 'Push…' : 'seatSelections → Firebase (2 yer)'}
                </Button>
              </Stack>

              {selectedSelStatus === 2 && (
                <Box sx={{ p: 1.5, mb: 1.5, bgcolor: '#fffbeb', borderRadius: 1, border: '1px solid #f59e0b' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Hold — gezen user
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    User ID: {selectedSeatSel?.user || '—'}
                    {selectedSeatSel?.statusChangedAt
                      ? ` · ${selectedSeatSel.statusChangedAt}`
                      : ''}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={loadingSeatSelUser || !selectedSeatSel?.user}
                    onClick={loadSeatSelUser}
                    sx={{ mb: 1 }}
                  >
                    {loadingSeatSelUser ? 'Yükleniyor…' : 'User bilgisini getir'}
                  </Button>
                  {seatSelUser && (
                    <Box>
                      <Typography variant="body2">
                        <strong>
                          {seatSelUser._missing
                            ? 'User kaydı yok'
                            : formatUserFullName(seatSelUser)}
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        ID: {seatSelUser.id || selectedSeatSel?.user}
                      </Typography>
                      {seatSelUser.email && (
                        <Typography variant="body2">Email: {seatSelUser.email}</Typography>
                      )}
                      {seatSelUser.mobile && (
                        <Typography variant="body2">Tel: {seatSelUser.mobile}</Typography>
                      )}
                      {(seatSelUser.role || seatSelUser.crole) && (
                        <Typography variant="body2">
                          Rol: {seatSelUser.role || seatSelUser.crole}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {!selectedSeatSel?.user && (
                    <Typography variant="body2" color="text.secondary">
                      Hold’da user yok — anonymous / süresi dolmuş olabilir
                    </Typography>
                  )}
                </Box>
              )}

              {selectedTickets.length === 0 && selectedSeatId && (
                <Box sx={{ p: 1.5, mb: 1.5, bgcolor: '#ecfdf5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    User ID ile satış / atama
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Boş koltukta satış açar; bloklu koltukta bloku kaldırıp bu user’a bilet + sale yazar
                    (Postgres). Firebase ayrı push.
                  </Typography>
                  <Stack direction="row" gap={1} alignItems="flex-start">
                    <TextField
                      size="small"
                      label="User ID"
                      placeholder="Firebase user id"
                      value={manualUserId}
                      onChange={(e) => setManualUserId(e.target.value.trim())}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={
                        assigningSale ||
                        creatingTicket ||
                        pushing ||
                        !manualUserId ||
                        loadingManualUser ||
                        manualUserPreview?._missing
                      }
                      onClick={handleAssignSaleByUserId}
                    >
                      {assigningSale ? '…' : 'Satış / mevcut bilete ekle'}
                    </Button>
                  </Stack>
                  {loadingManualUser && (
                    <Typography variant="caption" display="block" mt={0.5}>
                      User kontrol…
                    </Typography>
                  )}
                  {manualUserPreview && !manualUserPreview._missing && (
                    <Typography variant="body2" mt={0.5}>
                      {formatUserFullName(manualUserPreview)}
                      {manualUserPreview.email ? ` · ${manualUserPreview.email}` : ''}
                    </Typography>
                  )}
                  {manualUserPreview?._missing && (
                    <Typography variant="body2" color="error" mt={0.5}>
                      User bulunamadı
                    </Typography>
                  )}
                </Box>
              )}

              {selectedTickets.length === 0 && (
                <Box mb={1.5}>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Bu koltuğa bağlı bilet yok
                    {selectedSelStatus === 1 ? ' (seatSelections satılmış)' : ''}
                    {selectedSeatSel?.isBlockedSeat ? ' · isBlockedSeat' : ''}
                  </Typography>
                  {selectedSelStatus === 1 && (
                    <Box sx={{ p: 1, mb: 1, bgcolor: '#fff7ed', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        seatSelections.user (satın alan / işaretleyen)
                      </Typography>
                      {loadingSeatSelUser ? (
                        <Typography variant="body2" mb={1}>
                          User yükleniyor…
                        </Typography>
                      ) : seatSelUser ? (
                        <Box mb={1}>
                          <Typography variant="body2">
                            <strong>
                              {seatSelUser._missing
                                ? 'User kaydı yok'
                                : formatUserFullName(seatSelUser)}
                            </strong>
                          </Typography>
                          <Typography variant="body2">
                            ID: {seatSelUser.id || selectedSeatSel?.user}
                          </Typography>
                          {seatSelUser.email && (
                            <Typography variant="body2">Email: {seatSelUser.email}</Typography>
                          )}
                          {seatSelUser.mobile && (
                            <Typography variant="body2">Tel: {seatSelUser.mobile}</Typography>
                          )}
                          {(seatSelUser.role || seatSelUser.crole) && (
                            <Typography variant="body2">
                              Rol: {seatSelUser.role || seatSelUser.crole}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                            Normal akışta bu alan bileti alan user’dır. isBlockedSeat / admin ise
                            koltuğu işaretleyen panel user olabilir (satış değil).
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          seatSelections.user yok
                        </Typography>
                      )}

                      <Typography variant="subtitle2" gutterBottom>
                        Satış / order bilgisi
                      </Typography>
                      {loadingSales ? (
                        <Typography variant="body2">Aranıyor…</Typography>
                      ) : saleMatches.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Bu event’te sale bulunamadı
                          {selectedSeatSel?.isBlockedSeat
                            ? ' (isBlockedSeat — muhtemel blok, satış değil)'
                            : ''}
                          .
                        </Typography>
                      ) : (
                        saleMatches.slice(0, 3).map((m) => (
                          <Box key={m.saleId} sx={{ mb: 1 }}>
                            <Typography variant="body2">
                              <strong>Sale:</strong> {m.saleId}
                              {m.matchType === 'seat' ? ' (koltuk eşleşti)' : ' (user eşleşti)'}
                            </Typography>
                            <Typography variant="body2">
                              Order/OID: {m.orderId || m.oid || '—'}
                            </Typography>
                            <Typography variant="body2">
                              Payment: {m.paymentId || '—'} · status: {m.status ?? '—'}
                            </Typography>
                            <Typography variant="body2">
                              Alıcı: {formatUserFullName(m.user)}
                              {m.user?.email ? ` · ${m.user.email}` : ''}
                              {m.user?.id ? ` · ${m.user.id}` : ''}
                            </Typography>
                          </Box>
                        ))
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<ConfirmationNumberIcon />}
                        disabled={
                          creatingTicket ||
                          assigningSel ||
                          pushing ||
                          (selectedSeatSel?.isBlockedSeat === true && saleMatches.length === 0)
                        }
                        onClick={handleCreateMissingTicket}
                        sx={{ mt: 1 }}
                      >
                        {creatingTicket
                          ? 'Oluşturuluyor…'
                          : selectedSeatSel?.isBlockedSeat && saleMatches.length === 0
                            ? 'Bilet yok (bloklu koltuk)'
                            : 'Bilet oluştur (Postgres)'}
                      </Button>
                      {selectedSeatSel?.isBlockedSeat && saleMatches.length === 0 && (
                        <Typography variant="caption" color="warning.main" display="block" mt={1}>
                          Bu koltuk admin blok — bilet oluşturulmaz. Satışa açmak için Boşalt (0).
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
              {selectedTickets.map((t) => (
                <Box
                  key={t.id}
                  sx={{ mb: 1.5, p: 1, bgcolor: '#f1f5f9', borderRadius: 1 }}
                >
                  <Typography variant="body2">
                    <strong>{formatUserFullName(t.user)}</strong>
                  </Typography>
                  <Typography variant="body2">PNR: {t.pnr || '—'}</Typography>
                  <Typography variant="body2">
                    Ürün:{' '}
                    {t.product?.title?.tr ||
                      t.product?.title?.en ||
                      getTicketSeats(t).find((s) => s.id === selectedSeatId)?.selectedOption
                        ?.title?.tr ||
                      getTicketSeats(t).find((s) => s.id === selectedSeatId)?.selectedOption
                        ?.title?.en ||
                      getTicketSeats(t).find((s) => s.id === selectedSeatId)?.productID ||
                      t.product?.id ||
                      '—'}
                  </Typography>
                  <Stack direction="row" gap={1} alignItems="center" mt={1}>
                    <TextField
                      size="small"
                      label="Yeni koltuk"
                      placeholder="örn. E70"
                      value={seatEdits[t.id] ?? ''}
                      onChange={(e) =>
                        setSeatEdits((prev) => ({
                          ...prev,
                          [t.id]: e.target.value.toUpperCase(),
                        }))
                      }
                      sx={{ flex: 1 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={savingSeat === t.id || pushing || refundingTicketId === t.id}
                      onClick={() => handleChangeSeat(t)}
                    >
                      {savingSeat === t.id ? '…' : 'Değiştir'}
                    </Button>
                  </Stack>
                  <Stack direction="row" gap={1} mt={0.5} flexWrap="wrap">
                    <Button
                      size="small"
                      color="secondary"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      disabled={pushing || savingSeat === t.id || refundingTicketId === t.id}
                      onClick={() => handlePushTicketToFirebase(t)}
                    >
                      Bilet → Firebase
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={
                        refundingTicketId === t.id ||
                        savingSeat === t.id ||
                        pushing ||
                        isTicketRefunded(t)
                      }
                      onClick={() => handleRefundTicketOnSeat(t)}
                    >
                      {refundingTicketId === t.id
                        ? 'İade…'
                        : 'İade et (koltuk 1 kalır)'}
                    </Button>
                    <Button
                      size="small"
                      component={RouterLink}
                      to={`/tickets?eventID=${eventID}&pnr=${encodeURIComponent(t.pnr || '')}`}
                      endIcon={<OpenInNewIcon />}
                    >
                      Ticket’a git
                    </Button>
                  </Stack>
                </Box>
              ))}
              {selectedHold && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleClearHold}
                  disabled={clearingHold || assigningSel || pushing}
                  sx={{ mt: 1 }}
                >
                  {clearingHold || assigningSel ? 'Temizleniyor…' : 'Hold temizle (Postgres)'}
                </Button>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Satılmış: {analysis.soldSeatIds.length} · Admin rezerv:{' '}
            {analysis.blockedSeatIds?.length || 0}
            {adminBlockedWithTicketSeatIds.length > 0
              ? ` (${adminBlockedWithTicketSeatIds.length} biletli)`
              : ''}{' '}
            · Hold: {analysis.holdSeatIds.length} · Bilet: {tickets.length}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default SeatOpsPage;
